import Email, { MailsListId } from "@/domain/entities/emails/Email";
import MailSource from "@/domain/enums/MailSource";
import { queryKeys, STALE_TIME } from "@/lib/queryKeys";
import { emailMapper } from "@/mappers/email/emailMapper";
import {
    Mail,
    MailFolder,
    SmartLabel,
    useMailLists,
    useSdkEvent,
} from "@cs_teamwaydev/mailvista-sdk_react";
import {
    replaceEqualDeep,
    useInfiniteQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import useFetchErrorToast from "../utils/useFetchErrorToast";
import {
    nextFetchSeq,
    preserveEnrichedFields,
    rebaseStalePages,
} from "./mailsListCache";
import type { MailsListCache } from "./mailsListCache";

const PAGE_SIZE = 16;

interface FetchMailsOptions {
    offset: number;
    limit: number;
}

const isMailFolder = (listId: MailsListId): listId is MailFolder =>
    Object.values(MailFolder).includes(listId as unknown as MailFolder);

const isSmartLabel = (listId: MailsListId): listId is SmartLabel =>
    Object.values(SmartLabel).includes(listId as SmartLabel);

const isCustomView = (listId: MailsListId): boolean =>
    !isMailFolder(listId) && !isSmartLabel(listId);

const useGetMailsList = (listId: MailsListId) => {
    const mailLists = useMailLists();
    const queryClient = useQueryClient();
    const showFetchErrorToast = useFetchErrorToast();
    const updateVersionRef = useRef(0);

    const fetchMailsByType = async ({
        offset,
        limit,
    }: FetchMailsOptions): Promise<Mail[] | undefined> => {
        if (isMailFolder(listId)) {
            return mailLists.getGeneralMails(listId, offset, limit);
        }

        if (isSmartLabel(listId)) {
            return mailLists.getSmartLabelMails(listId, offset, limit);
        }

        if (isCustomView(listId)) {
            return mailLists.getCustomViewMails(listId, offset, limit);
        }

        return undefined;
    };

    const fetchMails = async ({
        pageParam,
    }: {
        pageParam: {
            currentPage: number;
            offset: number;
        };
    }): Promise<{
        data: Array<Email>;
        currentPage: number;
        nextPage: number | null;
        fetchSeq: number;
    }> => {
        const fetchSeq = nextFetchSeq();
        try {
            const mails = await fetchMailsByType({
                offset: pageParam.offset,
                limit: PAGE_SIZE,
            });

            if (!mails) {
                return {
                    data: [],
                    currentPage: pageParam.currentPage,
                    nextPage: null,
                    fetchSeq,
                };
            }

            const mappedMails = mails.map(emailMapper);

            // Filter duplicates based on uniqueMailvistaId (copie physique)
            const uniqueMails = mappedMails.filter(
                (mail, index, array) =>
                    array.findIndex(
                        (m) => m.uniqueMailvistaId === mail.uniqueMailvistaId,
                    ) === index,
            );

            let nextPage: number | null = pageParam.currentPage + 1;
            if (uniqueMails.length === 0) {
                nextPage = null; // For Smart Labels, received mails might be inferior to PAGE_SIZE but there are still mails to fetch
            }

            // Un snapshot de listing peut précéder la fin de l'enrichissement : sans
            // réinjection il écraserait les PJ et liens déjà connus du cache.
            const previousList = queryClient.getQueryData<MailsListCache>(
                queryKeys.mails.list(listId),
            );
            const data = preserveEnrichedFields(uniqueMails, previousList);

            return {
                data,
                currentPage: pageParam.currentPage,
                nextPage,
                fetchSeq,
            };
        } catch (error) {
            console.error("Error fetching mails:", error);
            showFetchErrorToast(error, {
                queryKey: queryKeys.mails.list(listId),
                titleTranslationKey: "errors.emails",
                onRetry: () =>
                    queryClient.refetchQueries({
                        queryKey: queryKeys.mails.list(listId),
                    }),
            });

            // Sans le throw, l'echec passe pour un succes vide et la liste se vide.
            throw error;
        }
    };

    const emailsList = useInfiniteQuery({
        queryKey: queryKeys.mails.list(listId),
        queryFn: fetchMails,
        getNextPageParam: (lastPage, allPages) => {
            if (!lastPage.nextPage) return undefined;

            if (isMailFolder(listId)) {
                // For basic lists, we can use mails count to deal with deleted or new emails.
                const totalDisplayedMails = allPages.reduce(
                    (acc, page) => acc + (page.data?.length ?? 0),
                    0,
                );

                return {
                    currentPage: lastPage.nextPage,
                    offset: totalDisplayedMails,
                };
            }

            // For Smart labels and Custom views, we might receive less than PAGE_SIZE mails for a page,
            // so the constant offset must be kept to fit the Graph pagination.
            // We do not deal with deleted, treated or new emails yet.

            return {
                currentPage: lastPage.nextPage,
                offset: lastPage.nextPage * PAGE_SIZE,
            };
        },
        initialPageParam: {
            currentPage: 0,
            offset: 0,
        },
        staleTime: STALE_TIME.realtime,
        // Seul point où l'on voit à la fois le cache courant et la reconstruction
        // périmée que le commit s'apprête à écrire.
        // ⚠️ L'option vit sur `query.options`, partagée par tous les observateurs de la
        // clé : un autre `useQuery` sur la même clé qui ne la porterait pas la
        // désactiverait silencieusement.
        structuralSharing: (previous, fresh) =>
            replaceEqualDeep(
                previous,
                rebaseStalePages(
                    previous as MailsListCache | undefined,
                    fresh as MailsListCache,
                ),
            ),
    });

    /**
     * ***************
     * EVENTS HANDLERS
     * ***************
     */

    // mail-updated is also handled in useGetMailThread (thread cache) and useVistassistant (assistant store).
    // Each hook updates its own data layer independently — no shared handler is used intentionally.
    const handleMailUpdate = (newMail: Mail) => {
        const updatedMail = emailMapper(newMail);

        // Increment version to trigger mosaic layout recalculation, in case mail format was changed
        updateVersionRef.current += 1;

        // On a smart-label list, a mail whose effective smart label no longer matches this list
        // has left the bucket (e.g. an unfocus moves it Focus -> PourInfo). The SDK also emits
        // custom-view-list-updated for the source label, but patching the mail in place here would
        // keep the stale mail visible until that refetch lands. Remove it from this list instead.
        // isUserProcessed forces the mail into Traités regardless of smartLabel (mirrors the SDK rule).
        // Sans label utilisable, on ne conclut rien (miroir de `getEffectiveSmartLabel`
        // côté mobile) : un `mail-updated` d'enrichissement seul ne déplace pas un mail.
        const effectiveLabel = updatedMail.isUserProcessed
            ? SmartLabel.Traites
            : updatedMail.smartLabel;
        const hasLeftThisSmartLabel =
            isSmartLabel(listId) &&
            effectiveLabel !== undefined &&
            effectiveLabel !== listId;

        queryClient.setQueryData<MailsListCache>(
            queryKeys.mails.list(listId),
            (oldData) => {
                if (!oldData?.pages) return oldData;

                const updatedPages = oldData.pages.map((page) => ({
                    ...page,
                    data: hasLeftThisSmartLabel
                        ? page.data.filter(
                              (mail) =>
                                  mail.uniqueMailvistaId !==
                                  updatedMail.uniqueMailvistaId,
                          )
                        : page.data.map((mail) =>
                              mail.uniqueMailvistaId ===
                              updatedMail.uniqueMailvistaId
                                  ? updatedMail
                                  : mail,
                          ),
                }));

                return { ...oldData, pages: updatedPages };
            },
        );
    };

    /* This event is only used to update a datacard (for example with added attachments, smart labels, etc.) */
    useSdkEvent("mail-updated", handleMailUpdate);

    const handleNewInboxMails = (newMails: Mail[]) => {
        if (!listId || listId !== MailSource.Inbox) return;

        const mappedNewMails = newMails.map(emailMapper);

        queryClient.setQueryData<MailsListCache>(
            queryKeys.mails.list(listId),
            (oldData) => {
                if (!oldData?.pages) return oldData;

                const allPages = [...oldData.pages];

                const existingUniqueIds = new Set(
                    allPages[0].data.map((mail) => mail.uniqueMailvistaId),
                );
                const uniqueNewMails = mappedNewMails.filter(
                    (mail) => !existingUniqueIds.has(mail.uniqueMailvistaId),
                );
                const updatedFirstPage = {
                    ...allPages[0],
                    data: [...uniqueNewMails, ...allPages[0].data],
                };
                allPages[0] = updatedFirstPage;

                return { ...oldData, pages: allPages };
            },
        );
    };

    /* This event is only used to update the inbox list in case of mails added */
    useSdkEvent("mails-new-mails", handleNewInboxMails);

    const handleCustomViewListUpdated = ({
        customViewId,
    }: {
        customViewId: string;
    }) => {
        if (customViewId === listId) {
            // refetchQueries (not invalidateQueries): a custom view mutation can change
            // the list contents drastically, which makes the infinite query pagination
            // offset invalid. An immediate refetch is required to restore consistency.
            queryClient.refetchQueries({
                queryKey: queryKeys.mails.list(listId),
            });
        }
    };

    /* This event is only used to update the custom view list in case of mails added or removed */
    useSdkEvent("custom-view-list-updated", handleCustomViewListUpdated);

    const flattenedMails = useMemo(() => {
        const pages = emailsList.data?.pages ?? [];
        const flat = pages.flatMap((page) => page.data ?? []);
        const seenIds = new Set<string>();

        return flat.filter((mail) => {
            if (seenIds.has(mail.uniqueMailvistaId)) return false;

            seenIds.add(mail.uniqueMailvistaId);

            return true;
        });
    }, [emailsList.data?.pages]);

    return {
        ...emailsList,
        flattenedMails,
        updateVersion: updateVersionRef.current,
    };
};

export default useGetMailsList;
